// Shared UI primitives: Toast, Modal, Drawer, Combobox (typeahead + add on the fly), DatePicker, Field, StatusChip, Stamp, EmptyState, Pager.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import type { Status } from '../types';
import { STATUS_LABEL } from '../types';

// ------------------------------------------------------------------ toasts
type Toast = { id: number; text: string; kind: 'ok' | 'error' | 'info' };
const ToastCtx = createContext<(text: string, kind?: Toast['kind']) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = (text: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, text, kind }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), kind === 'error' ? 6000 : 3500);
  };
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map(t => <div key={t.id} className={'toast ' + t.kind}><Icon name={t.kind === 'error' ? 'alert' : t.kind === 'ok' ? 'check' : 'info'} />{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

// ------------------------------------------------------------------ modal
export function Modal({ title, onClose, children, footer, size, icon }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; size?: 'sm' | 'lg' | 'xl'; icon?: string }) {
  const self = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Esc closes only the top-most window
    const onKey = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; const all = document.querySelectorAll('.backdrop'); if (all[all.length - 1] === self.current) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="backdrop" ref={self} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'modal ' + (size || '')} role="dialog" aria-modal="true">
        <div className="m-head">{icon && <Icon name={icon} />}<h2>{title}</h2><button className="icon-btn" onClick={onClose} aria-label="Închide"><Icon name="x" /></button></div>
        <div className="m-body">{children}</div>
        {footer && <div className="m-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Confirm({ title, text, onYes, onNo, danger, yesLabel }: { title: string; text: React.ReactNode; onYes: () => void; onNo: () => void; danger?: boolean; yesLabel?: string }) {
  return (
    <Modal title={title} onClose={onNo} size="sm" footer={<><span className="grow" /><button className="btn" onClick={onNo}>Renunță</button><button className={'btn ' + (danger ? 'danger' : 'primary')} onClick={onYes}>{yesLabel || 'Confirmă'}</button></>}>
      <p>{text}</p>
    </Modal>
  );
}

export function Drawer({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.backdrop')) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="drawer-wrap">
      <div className="scrim" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------------ field
export function Field({ label, required, hint, error, children, className }: { label?: React.ReactNode; required?: boolean; hint?: string; error?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={'field ' + (className || '')}>
      {label && <label>{label}{required && <span className="req">*</span>}</label>}
      {children}
      {error ? <span className="error">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: React.ReactNode }) {
  return <label className="row" style={{ cursor: 'pointer' }}><button type="button" role="switch" aria-checked={on} className={'switch ' + (on ? 'on' : '')} onClick={() => onChange(!on)} />{label && <span>{label}</span>}</label>;
}

// ------------------------------------------------------------------ status / stamp
/** {@code part}: a department's own answer — "Rezolvat" (the document itself becomes "Finalizat" only after distribution). */
export function StatusChip({ status, part }: { status: Status; part?: boolean }) {
  return <span className={'chip ' + status}>{part && status === 'RESOLVED' ? 'Rezolvat' : STATUS_LABEL[status] || status}</span>;
}
export function Stamp({ value, lg }: { value?: string | null; lg?: boolean }) {
  if (!value) return <span className="muted">—</span>;
  return <span className={'stamp ' + (lg ? 'lg' : '')}>{value}</span>;
}

export function EmptyState({ icon, title, text, action }: { icon?: string; title: string; text?: string; action?: React.ReactNode }) {
  return <div className="empty"><Icon name={icon || 'inbox'} /><h3>{title}</h3>{text && <p>{text}</p>}{action}</div>;
}

export function Pager({ page, size, total, onPage }: { page: number; size: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / size));
  if (total <= size) return null;
  return (
    <div className="pager">
      <span>{(page - 1) * size + 1}–{Math.min(total, page * size)} din {total}</span>
      <button className="btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)}><Icon name="chevronLeft" /></button>
      <span>{page} / {pages}</span>
      <button className="btn sm" disabled={page >= pages} onClick={() => onPage(page + 1)}><Icon name="chevronRight" /></button>
    </div>
  );
}

// ------------------------------------------------------------------ combobox
export interface ComboOption { value: string | number; label: string; sub?: string; color?: string | null }
interface ComboProps {
  value: string | number | null | undefined;
  options: ComboOption[];
  onChange: (value: string | number | null, option?: ComboOption) => void;
  allowAdd?: boolean;
  onAdd?: (text: string) => Promise<ComboOption | void> | ComboOption | void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
  id?: string;
}
/** Typeahead select: typing filters the list; with allowAdd the typed text can be added on the fly. */

/** A dropdown rendered in <body> with fixed position under (or above, when there is no room) its anchor — never clipped by a
 *  parent with overflow:hidden (form sections, cards, modals). Follows the anchor on scroll/resize. */
export function Floating({ anchor, open, children, className, maxH = 280, width, onMouseDown, innerRef }: {
  anchor: React.RefObject<HTMLElement>; open: boolean; children: React.ReactNode; className: string; maxH?: number; width?: number;
  onMouseDown?: (e: React.MouseEvent) => void; innerRef?: React.RefObject<HTMLDivElement>;
}) {
  const [r, setR] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!open) return;
    const upd = () => anchor.current && setR(anchor.current.getBoundingClientRect());
    upd();
    window.addEventListener('scroll', upd, true); window.addEventListener('resize', upd);
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd); };
  }, [open]);
  if (!open || !r) return null;
  const below = window.innerHeight - r.bottom - 12, above = r.top - 12;
  const up = below < Math.min(maxH, 200) && above > below;
  const h = Math.max(120, Math.min(maxH, up ? above : below));
  const w = width || r.width;
  const style: React.CSSProperties = { position: 'fixed', minWidth: 0, left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)), width: w, maxHeight: h, zIndex: 300,
    ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) };
  return createPortal(<div ref={innerRef} className={className} style={style} onMouseDown={onMouseDown}>{children}</div>, document.body);
}

export function Combobox({ value, options, onChange, allowAdd, onAdd, placeholder, disabled, invalid, clearable = true, id }: ComboProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [hl, setHl] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const selected = options.find(o => String(o.value) === String(value ?? ''));
  const display = open ? text : (selected ? selected.label : (value != null && value !== '' ? String(value) : ''));

  const filtered = useMemo(() => {
    const q = norm(text);
    if (!q) return options;
    return options.filter(o => norm(o.label).includes(q) || norm(String(o.value)).includes(q) || (o.sub && norm(o.sub).includes(q)));
  }, [text, options]);
  const exact = filtered.some(o => norm(o.label) === norm(text) || norm(String(o.value)) === norm(text));
  const canAdd = allowAdd && text.trim().length > 0 && !exact;
  const items = canAdd ? filtered.length + 1 : filtered.length;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (ref.current && !ref.current.contains(t) && !(pop.current && pop.current.contains(t))) close(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const openIt = () => { if (disabled) return; setText(''); setHl(0); setOpen(true); };
  const close = () => { setOpen(false); setText(''); };
  const pick = (o: ComboOption) => { onChange(o.value, o); close(); };
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    if (onAdd) { const r = await onAdd(t); if (r) { onChange(r.value, r); close(); return; } }
    onChange(t, { value: t, label: t });
    close();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { openIt(); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { setHl(h => Math.min(items - 1, h + 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setHl(h => Math.max(0, h - 1)); e.preventDefault(); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hl < filtered.length) pick(filtered[hl]); else if (canAdd) add(); }
    else if (e.key === 'Escape') close();
    else if (e.key === 'Tab') close();
  };

  return (
    <div className="combo" ref={ref}>
      <input id={id} className={'input ' + (invalid ? 'invalid' : '')} value={display} placeholder={placeholder || 'Alegeți sau tastați…'} disabled={disabled}
        onFocus={openIt} onClick={openIt} onChange={e => { setText(e.target.value); setHl(0); if (!open) setOpen(true); }} onKeyDown={onKey} autoComplete="off" role="combobox" aria-expanded={open} />
      {clearable && value != null && value !== '' && !disabled && !open
        ? <button type="button" className="icon-btn" style={{ position: 'absolute', right: 2, top: 2, width: 28, height: 28 }} onClick={() => onChange(null)} aria-label="Șterge"><Icon name="x" size={14} /></button>
        : <Icon name="chevronDown" className="caret" size={16} />}
      {open && (
        <Floating anchor={ref} open={open} className="popover" innerRef={pop}>
          {filtered.length === 0 && !canAdd && <div className="none">{allowAdd ? 'Tastați pentru a adăuga o valoare nouă' : 'Nicio potrivire'}</div>}
          {filtered.slice(0, 200).map((o, i) => (
            <div key={String(o.value)} className={'opt ' + (i === hl ? 'hl' : '')} role="option" aria-selected={String(o.value) === String(value ?? '')} onMouseEnter={() => setHl(i)} onMouseDown={e => { e.preventDefault(); pick(o); }}>
              {o.color && <span className="color-dot" style={{ background: o.color }} />}
              <span>{o.label}</span>{o.sub && <span className="sub">{o.sub}</span>}
              {String(o.value) === String(value ?? '') && <Icon name="check" size={14} />}
            </div>
          ))}
          {canAdd && <div className={'opt add ' + (hl === filtered.length ? 'hl' : '')} onMouseEnter={() => setHl(filtered.length)} onMouseDown={e => { e.preventDefault(); add(); }}><Icon name="plus" size={14} />Adaugă „{text.trim()}”</div>}
        </Floating>
      )}
    </div>
  );
}
export function norm(s: string) { return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim(); }

// ------------------------------------------------------------------ date picker
const MONTHS = ['ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie', 'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie'];
const DOWS = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];
export function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}
export function todayIso() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function parseTyped(s: string): string | null {
  const t = s.trim();
  let m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(t);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) return t;
  return null;
}

/** Calendar date picker with typing support (dd.mm.yyyy). Value is ISO yyyy-mm-dd. */
export function DatePicker({ value, onChange, disabled, invalid, id, placeholder }: { value?: string | null; onChange: (iso: string | null) => void; disabled?: boolean; invalid?: boolean; id?: string; placeholder?: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(fmtDate(value));
  const [view, setView] = useState(() => { const d = value ? new Date(value) : new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const ref = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  useEffect(() => { setText(fmtDate(value)); }, [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { const t = e.target as Node; if (ref.current && !ref.current.contains(t) && !(pop.current && pop.current.contains(t))) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const commitText = () => {
    if (!text.trim()) { onChange(null); return; }
    const iso = parseTyped(text);
    if (iso) { onChange(iso); const d = new Date(iso); setView({ y: d.getFullYear(), m: d.getMonth() }); } else setText(fmtDate(value));
  };
  const first = new Date(view.y, view.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const cells: { iso: string; day: number; other: boolean }[] = [];
  const start = new Date(view.y, view.m, 1 - startOffset);
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, day: d.getDate(), other: d.getMonth() !== view.m });
  }
  const today = todayIso();
  return (
    <div className="combo" ref={ref}>
      <input id={id} className={'input ' + (invalid ? 'invalid' : '')} value={text} placeholder={placeholder || 'zz.ll.aaaa'} disabled={disabled} onChange={e => setText(e.target.value)}
        onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onBlur={commitText} onKeyDown={e => { if (e.key === 'Enter') { commitText(); setOpen(false); } if (e.key === 'Escape') setOpen(false); }} autoComplete="off" />
      <Icon name="calendar" className="caret" size={16} />
      {open && !disabled && (
        <Floating anchor={ref} open className="calendar" width={280} maxH={360} innerRef={pop} onMouseDown={e => e.preventDefault()}>
          <div className="head">
            <button type="button" className="icon-btn" onClick={() => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 })} aria-label="Luna anterioară"><Icon name="chevronLeft" /></button>
            <b>{MONTHS[view.m]} {view.y}</b>
            <button type="button" className="icon-btn" onClick={() => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 })} aria-label="Luna următoare"><Icon name="chevronRight" /></button>
          </div>
          <div className="grid">
            {DOWS.map(d => <div key={d} className="dow">{d}</div>)}
            {cells.map(c => <button type="button" key={c.iso} className={'day ' + (c.other ? 'other ' : '') + (c.iso === today ? 'today ' : '') + (c.iso === value ? 'sel' : '')} onClick={() => { onChange(c.iso); setOpen(false); }}>{c.day}</button>)}
          </div>
          <div className="foot">
            <button type="button" onClick={() => { onChange(today); setOpen(false); }}>Astăzi</button>
            <button type="button" onClick={() => { onChange(null); setOpen(false); }}>Șterge</button>
          </div>
        </Floating>
      )}
    </div>
  );
}

export function fmtBytes(n?: number | null) {
  if (n == null) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}

export function initials(name?: string) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

// ------------------------------------------------------------------ dual column header: filter (text / list / date) + arrow to sort
export interface ColumnHeaderProps {
  label: string;
  sortDir?: 'asc' | 'desc' | null;
  onSort?: () => void;
  filter?: string;
  onFilter?: (v: string) => void;
  /** 'text' (default when onFilter is set), 'select' (needs options) or 'date' (single date or interval yyyy-mm-dd..yyyy-mm-dd) */
  kind?: 'text' | 'select' | 'date';
  options?: { value: string; label: string }[];
  minWidth?: number;
}
export function ColumnHeader({ label, sortDir, onSort, filter = '', onFilter, kind, options, minWidth }: ColumnHeaderProps) {
  const k = kind || (options ? 'select' : 'text');
  const [text, setText] = useState(filter);
  const [dateOpen, setDateOpen] = useState<DOMRect | null>(null);
  useEffect(() => { setText(filter); }, [filter]);
  useEffect(() => {
    if (!onFilter || k !== 'text' || text === filter) return;
    const t = setTimeout(() => onFilter(text), 350);
    return () => clearTimeout(t);
  }, [text]);
  const active = !!filter;
  return (
    <th className={'dual-th ' + (active ? 'filtered' : '')} style={minWidth ? { minWidth } : undefined} aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}>
      <div className="dual">
        {!onFilter ? <span className="dual-label">{label}</span> : k === 'select' ? (
          <select className="dual-input" value={filter} onChange={e => onFilter(e.target.value)} title={`Filtrează după ${label}`}>
            <option value="">{label}</option>
            {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : k === 'date' ? (
          <button type="button" className="dual-input dual-date" title={`Filtrează după ${label} (o zi sau un interval)`} onClick={e => setDateOpen((e.currentTarget as HTMLElement).getBoundingClientRect())}>
            {filter ? fmtRange(filter) : label}<Icon name="calendar" size={13} />
          </button>
        ) : (
          <input className="dual-input" type="search" autoComplete="off" spellCheck={false} value={text} placeholder={label} title={`Tastați pentru a filtra după ${label}`}
            onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') { setText(''); onFilter(''); } if (e.key === 'Enter') onFilter(text); }} />
        )}
        {active && onFilter && <button type="button" className="dual-clear" title="Șterge filtrul" onClick={() => { setText(''); onFilter(''); }}><Icon name="x" size={12} /></button>}
        {onSort && <button type="button" className={'dual-sort ' + (sortDir ? 'on' : '')} onClick={onSort} title={sortDir === 'asc' ? 'Sortat crescător — apăsați pentru descrescător' : sortDir === 'desc' ? 'Sortat descrescător — apăsați pentru crescător' : 'Sortează după această coloană'}>
          <Icon name={sortDir === 'asc' ? 'arrowUp' : 'arrowDown'} size={13} />
        </button>}
      </div>
      {dateOpen && onFilter && <DateRangePopover anchor={dateOpen} value={filter} onChange={v => { onFilter(v); setDateOpen(null); }} onClose={() => setDateOpen(null)} />}
    </th>
  );
}

export function fmtRange(v: string) {
  const [a, b] = v.split('..');
  if (!b || a === b) return fmtDate(a);
  const [ya, ma] = a.split('-'); const [yb] = b.split('-');
  return (ya === yb ? fmtDate(a).slice(0, 5) : fmtDate(a)) + ' – ' + fmtDate(b);
}

/** Floating calendar: pick one day, or an interval (first click = start, second click = end). */
export function DateRangePopover({ anchor, value, onChange, onClose }: { anchor: DOMRect; value: string; onChange: (v: string) => void; onClose: () => void }) {
  const [from0, to0] = (value || '').split('..');
  const [mode, setMode] = useState<'day' | 'range'>(to0 && to0 !== from0 ? 'range' : 'day');
  const [start, setStart] = useState<string | null>(mode === 'range' ? from0 || null : null);
  const [end, setEnd] = useState<string | null>(mode === 'range' ? to0 || null : null);
  const [hover, setHover] = useState<string | null>(null);
  const init = from0 ? new Date(from0 + 'T00:00:00') : new Date();
  const [view, setView] = useState({ y: init.getFullYear(), m: init.getMonth() });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, []);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const first = new Date(view.y, view.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const cells: { iso: string; day: number; other: boolean }[] = [];
  for (let i = 0; i < 42; i++) { const d = new Date(view.y, view.m, 1 - startOffset + i); cells.push({ iso: iso(d), day: d.getDate(), other: d.getMonth() !== view.m }); }
  const today = todayIso();
  const lo = start && (end || hover) ? (start < (end || hover)! ? start : (end || hover)!) : start;
  const hi = start && (end || hover) ? (start < (end || hover)! ? (end || hover)! : start) : start;
  const click = (d: string) => {
    if (mode === 'day') { onChange(d); return; }
    if (!start || end) { setStart(d); setEnd(null); return; }
    const a = d < start ? d : start, b = d < start ? start : d;
    setEnd(b); setStart(a); onChange(`${a}..${b}`);
  };
  const monthRange = () => { const a = new Date(view.y, view.m, 1), b = new Date(view.y, view.m + 1, 0); onChange(`${iso(a)}..${iso(b)}`); };
  const lastDays = (n: number) => { const b = new Date(), a = new Date(); a.setDate(a.getDate() - (n - 1)); onChange(`${iso(a)}..${iso(b)}`); };
  const left = Math.min(anchor.left, window.innerWidth - 300);
  return (
    <div ref={ref} className="calendar range-cal" style={{ position: 'fixed', top: anchor.bottom + 6, left: Math.max(8, left) }} onMouseDown={e => e.stopPropagation()}>
      <div className="segmented" style={{ width: '100%', marginBottom: 10 }}>
        <button className={mode === 'day' ? 'active' : ''} style={{ flex: 1 }} onClick={() => { setMode('day'); setStart(null); setEnd(null); }}>O zi</button>
        <button className={mode === 'range' ? 'active' : ''} style={{ flex: 1 }} onClick={() => setMode('range')}>Interval</button>
      </div>
      <div className="head">
        <button type="button" className="icon-btn" onClick={() => setView(v => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 })} aria-label="Luna anterioară"><Icon name="chevronLeft" /></button>
        <b>{MONTHS[view.m]} {view.y}</b>
        <button type="button" className="icon-btn" onClick={() => setView(v => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 })} aria-label="Luna următoare"><Icon name="chevronRight" /></button>
      </div>
      <div className="grid" onMouseLeave={() => setHover(null)}>
        {DOWS.map(d => <div key={d} className="dow">{d}</div>)}
        {cells.map(c => {
          const sel = mode === 'day' ? c.iso === from0 && !to0 || (c.iso === from0 && to0 === from0) : (c.iso === lo || c.iso === hi);
          const inRange = mode === 'range' && lo && hi && c.iso > lo && c.iso < hi;
          return <button type="button" key={c.iso} className={'day ' + (c.other ? 'other ' : '') + (c.iso === today ? 'today ' : '') + (sel ? 'sel ' : '') + (inRange ? 'inrange' : '')}
            onMouseEnter={() => mode === 'range' && start && !end && setHover(c.iso)} onClick={() => click(c.iso)}>{c.day}</button>;
        })}
      </div>
      <div className="muted small" style={{ margin: '8px 2px 0' }}>{mode === 'range' ? (start && !end ? 'Alegeți ziua de sfârșit' : 'Alegeți ziua de început, apoi ziua de sfârșit') : 'Alegeți o zi'}</div>
      <div className="foot" style={{ flexWrap: 'wrap', gap: 2 }}>
        <button type="button" onClick={() => onChange(today)}>Astăzi</button>
        <button type="button" onClick={() => lastDays(7)}>Ultimele 7 zile</button>
        <button type="button" onClick={monthRange}>Toată luna</button>
        <button type="button" onClick={() => onChange('')}>Șterge</button>
      </div>
    </div>
  );
}

/** Column picker: which columns are visible (saved per user by the caller).
 *  as="th": the small icon in the table's header corner; as="button": a labelled toolbar button people actually notice. */
export function ColumnPicker({ columns, visible, onChange, onReset, as = 'th' }: { columns: { key: string; label: string }[]; visible: string[]; onChange: (keys: string[]) => void; onReset: () => void; as?: 'th' | 'button' }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!anchor) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAnchor(null); };
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [anchor]);
  const toggle = (k: string) => {
    const on = visible.includes(k);
    if (on && visible.length <= 1) return;
    onChange(on ? visible.filter(x => x !== k) : columns.map(c => c.key).filter(x => x === k || visible.includes(x)));
  };
  const open = (e: React.MouseEvent) => setAnchor(anchor ? null : (e.currentTarget as HTMLElement).getBoundingClientRect());
  const hidden = columns.length - visible.length;
  const popover = anchor && (
    <div ref={ref} className="popover col-picker" style={{ position: 'fixed', top: anchor.bottom + 6, left: Math.min(Math.max(8, anchor.right - 300), window.innerWidth - 308), width: 300, minWidth: 0, maxHeight: Math.max(260, window.innerHeight - anchor.bottom - 18) }}>
      <div className="col-picker-head"><b>Coloane afișate în listă</b><span className="muted small">{visible.length} din {columns.length}</span></div>
      <p className="muted small col-picker-hint">Bifați ce coloane vreți să vedeți. Alegerea se păstrează pentru contul dvs.</p>
      <div className="col-picker-list">
        {columns.map(c => (
          <label key={c.key} className="opt checkbox" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={visible.includes(c.key)} onChange={() => toggle(c.key)} />
            <span>{c.label}</span>
          </label>
        ))}
      </div>
      <div className="col-picker-foot">
        <button type="button" className="btn sm ghost" onClick={() => onChange(columns.map(c => c.key))}>Toate</button>
        <button type="button" className="btn sm ghost" onClick={onReset}>Implicit</button>
        <span className="grow" />
        <button type="button" className="btn sm primary" onClick={() => setAnchor(null)}>Gata</button>
      </div>
    </div>
  );
  if (as === 'button') return (
    <>
      <button type="button" className={'btn tool-wide col-picker-toolbar ' + (anchor ? 'on' : '')} onClick={open} aria-haspopup="dialog" aria-expanded={!!anchor}
        title="Alegeți ce coloane apar în listă">
        <Icon name="columns" /><span className="tb-label">Coloane</span><span className="col-count">{visible.length}/{columns.length}</span>
      </button>
      {popover}
    </>
  );
  return (
    <th className="col-picker-th">
      <button type="button" className={'icon-btn col-picker-btn ' + (anchor ? 'on' : '')} title={`Alegeți coloanele afișate${hidden > 0 ? ` (${hidden} ascunse)` : ''}`} aria-label="Alegeți coloanele afișate"
        onClick={open}><Icon name="columns" />{hidden > 0 && <span className="col-hidden-dot" />}</button>
      {popover}
    </th>
  );
}

/** Countdown badge: green > 10 days, amber ≤ 10, red when overdue. */
export function DaysLeft({ deadline, resolved, inline }: { deadline?: string | null; resolved?: boolean; inline?: boolean }) {
  if (!deadline) return null;
  const d = new Date(deadline.slice(0, 10) + 'T00:00:00'); const t = new Date(); t.setHours(0, 0, 0, 0);
  const n = Math.round((d.getTime() - t.getTime()) / 86400000);
  if (resolved) return <span className={'days-left ok ' + (inline ? 'inline' : '')}>rezolvat</span>;
  const cls = n < 0 ? 'over' : n <= 10 ? 'soon' : 'ok';
  const txt = n < 0 ? `depășit cu ${-n} ${-n === 1 ? 'zi' : 'zile'}` : n === 0 ? 'expiră azi' : `${n} ${n === 1 ? 'zi' : 'zile'} rămase`;
  return <span className={'days-left ' + cls + (inline ? ' inline' : '')}>{txt}</span>;
}
